import { Button } from '../../ui/button';
import { InputFieldset } from 'ui/inputFieldset';
import { CheckboxFieldset } from 'ui/checkboxFieldset';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, Upload, Users, X } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group'
import { getUserDetailsByITS } from '../../services/newRegistrationService';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { PartnerFormErrors } from '../../types/tourOperator';
import { formatFileSizeFromKB, maskEmail, maskMobileNumber, removeFullSpecialCharacters, validatePhoneNumberInput } from '../../utils/commonutils';

interface DocumentHistory {
    id: string;
    uploadedDate: string;
    uploadedTime: string;
    status: 'pending' | 'approved' | 'rejected';
    historyStatus: 'pending' | 'approved' | 'rejected';
    remarks?: string;
    uploadedUrl: string;
    reviewedBy?: string;
    reviewedDate?: string;
    reviewedTime?: string;
}

interface DocumentUpload {
    id: string;
    documentMasterId: string;
    name: string;
    status: string;
    file: File | null;
    isRequired: boolean;
    uploadedUrl?: string;
    history?: DocumentHistory[];
    currentStatus?: 'pending' | 'approved' | 'rejected';
    currentRemarks?: string;
    acceptedFormats?: string,
    maxFileSizeKB?: number | string;
}

interface Partner {
    id: string;
    isNonItsPartner: boolean,
    itsNumber: string;
    name: string;
    fullName: string;
    fatherName: string;
    email: string;
    mobileNumber: string;
    dateOfBirth: string;
    address: string;
    role: string;
    isPartner: boolean;
    isOfficeStaff: boolean;
    loginRequired: boolean;
    isAuthorizedGroupLeader: boolean;
    documents: DocumentUpload[];
    addedAfterApproval?: boolean;
}

interface AddNewPartnerPopupProps {
    isPartnerDialogOpen: boolean;
    setIsPartnerDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    partnerFormData: Partner;
    setPartnerFormData: React.Dispatch<React.SetStateAction<Partner>>;
    handleSavePartner: () => void;
    handlePartnerFileUpload: (docId: string, file: File | null) => void;
    AddpartnerErrors: PartnerFormErrors;
    setAddPartnerErrors?: React.Dispatch<React.SetStateAction<PartnerFormErrors>>;
    isSoleProprietor?: boolean;
}

const AddNewPartnerPopup = ({ isPartnerDialogOpen, setIsPartnerDialogOpen, partnerFormData, setPartnerFormData, handleSavePartner, handlePartnerFileUpload, AddpartnerErrors, setAddPartnerErrors, isSoleProprietor = false }: AddNewPartnerPopupProps) => {
    const [isFetchingPartnerITS, setIsFetchingPartnerITS] = useState(false);
    const [partnerITSError, setPartnerITSError] = useState('');

    useEffect(() => {
        if (!isSoleProprietor) return;

        setPartnerFormData((previousData) => ({
            ...previousData,
            isPartner: false,
            isOfficeStaff: true,
            role: 'office_staff',
        }));
        setAddPartnerErrors?.((errors) => ({
            ...errors,
            memberType: undefined,
            documents: undefined,
        }));
    }, [isSoleProprietor, setAddPartnerErrors, setPartnerFormData]);


    // RESET PARTNER FORM ITS DETAILS
    useEffect(() => {
        setPartnerFormData(prev => ({
            ...prev,
            itsNumber: partnerFormData.isNonItsPartner ? "99999999" : "",
            name: "",
            email: "",
            mobileNumber: "",
            // fullName: "",
            // fatherName: "",
            // dateOfBirth: "",
            // address: "",
        }));
    }, [partnerFormData.isNonItsPartner, setPartnerFormData]);


    // FETCH ITS DETAILS
    const handleFetchPartnerITS = async (itsNumber: string) => {
        if (itsNumber.length !== 8) {
            setPartnerITSError('Please enter a valid 8-digit ITS number');
            return;
        }

        // if (partnerFormData.isNonItsPartner) {
        //     setPartnerFormData((prev) => ({ ...prev, itsNumber: '99999999' }))
        //     return;
        // }

        setIsFetchingPartnerITS(true);
        setPartnerITSError('');
        try {
            const itsDatas = await getUserDetailsByITS(itsNumber, true)
            if (!itsDatas?.mumin_Id && !itsDatas?.muminDataId) {
                toast.info("No record found for this ITS.");
                setPartnerITSError("Please enter valid ITS number")
                setPartnerFormData(prev => ({
                    ...prev,
                    itsNumber: "",
                    name: "",
                    email: "",
                    mobile: "",
                }));
                return;
            } else {
                // Auto-populate partner fields from ITS data
                setPartnerFormData({
                    ...partnerFormData,
                    itsNumber: itsDatas?.mumin_Id || "",
                    name: `${itsDatas.first_Name} ${itsDatas.middle_Name} ${itsDatas.surname}`,
                    email: itsDatas.email || partnerFormData.email,
                    mobileNumber: itsDatas.mobile_No || partnerFormData.mobileNumber,
                });
                toast.success('Partner ITS details fetched successfully');
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : "ITS Number not found in the system. Please verify and try again.";
            setPartnerITSError(errorMessage || 'ITS Number not found');
            toast.error(errorMessage || 'Failed to fetch ITS details');
        } finally {
            setIsFetchingPartnerITS(false);
        }
    };



    const validateFile = (doc: DocumentUpload, file: File): boolean => {
        // ------------------ FORMAT VALIDATION ------------------
        if (doc.acceptedFormats) {
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            const allowedFormats = doc.acceptedFormats
                .split(",")
                .map((f) => f.trim().toLowerCase());
            if (!fileExtension || !allowedFormats.includes(fileExtension)) {
                toast.error(
                    `Accepted format: ${allowedFormats.join(", ")} Only`
                );
                return false;
            }
        }

        // ------------------ SIZE VALIDATION ------------------
        if (doc.maxFileSizeKB) {
            const maxSizeBytes = Number(doc.maxFileSizeKB) * 1024;
            // allow 5KB tolerance
            const tolerance = 5 * 1024;
            if (file.size > maxSizeBytes + tolerance) {
                toast.error(
                    `File size must be less than or equal to ${formatFileSizeFromKB(
                        doc.maxFileSizeKB
                    )}`
                );
                return false;
            }
        }
        return true;
    };


    return (
        <>
            <Dialog open={isPartnerDialogOpen} onOpenChange={setIsPartnerDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#5B9BD5]" />
                            {'Add Member'}
                        </DialogTitle>
                        <DialogDescription>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Basic Information */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-700 tracking-wide">Basic Information</h3>
                            {/* ITS Fetch Section - Auto-fetch on 8th digit */}
                            <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <div className="relative">
                                            <InputFieldset
                                                label="ITS Number"
                                                required
                                                id="partnerITS"
                                                value={partnerFormData.itsNumber}
                                                readOnly={partnerFormData.isNonItsPartner}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    setPartnerFormData(prev => ({
                                                        ...prev,
                                                        itsNumber: value,
                                                    }));
                                                    setPartnerITSError('');
                                                    // Auto-fetch when 8 digits are entered
                                                    if (value.length === 8) {
                                                        handleFetchPartnerITS(value); // ✅ pass value
                                                    }
                                                }}
                                                placeholder="Enter 8-digit ITS number"
                                                maxLength={8}
                                                className={`${partnerFormData.isNonItsPartner
                                                    ? "cursor-not-allowed bg-gray-100"
                                                    : ""
                                                    }`}
                                                disabled={isFetchingPartnerITS}
                                            />
                                            {AddpartnerErrors.itsNumber && (
                                                <p className="text-xs text-red-600 mt-1">
                                                    {AddpartnerErrors.itsNumber}
                                                </p>
                                            )}
                                            {isFetchingPartnerITS && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <Loader2 className="w-4 h-4 animate-spin text-[#5B9BD5]" />
                                                </div>
                                            )}
                                            {partnerITSError && (
                                                <p className="text-xs text-red-600">{partnerITSError}</p>
                                            )}
                                            {isFetchingPartnerITS && (
                                                <p className="text-xs text-[#5B9BD5]">Fetching ITS details...</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <CheckboxFieldset
                      label="User Type"
                      className="bg-white border-gray-300 rounded-lg"
                    >
                                        <div className="flex items-center gap-3">
                                            <Checkbox
                                                checked={partnerFormData.isNonItsPartner}
                                                onCheckedChange={(checked) =>
                                                    setPartnerFormData(prev => ({
                                                        ...prev,
                                                        isNonItsPartner: !!checked,
                                                    }))
                                                }
                                            />
                                            <label
                                                htmlFor={`partner-non-its`}
                                                className="text-sm text-gray-700 cursor-pointer"
                                            >
                                                Non ITS User
                                            </label>
                                        </div>
                                        </CheckboxFieldset>
                                    </div>
                                    <div className="space-y-2">
                                        <InputFieldset
                                            label="Full Name"
                                            required
                                            id="partnerName"
                                            value={partnerFormData.name}
                                            readOnly={!partnerFormData.isNonItsPartner}
                                            onChange={(e) => {
                                                const value = removeFullSpecialCharacters(e.target.value);
                                                setPartnerFormData({ ...partnerFormData, name: value })
                                            }}
                                            placeholder="Enter full name"
                                            className={`${!partnerFormData.isNonItsPartner
                                                ? "cursor-not-allowed bg-gray-100"
                                                : ""
                                                }`}
                                        />
                                        {AddpartnerErrors.name && (
                                            <p className="text-xs text-red-600 mt-1">
                                                {AddpartnerErrors.name}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <InputFieldset
                                            label="Email"
                                            required
                                            id="partnerEmail"
                                            type="email"
                                            // value={partnerFormData.email}
                                            value={
                                                partnerFormData.isNonItsPartner
                                                    ? partnerFormData.email
                                                    : maskEmail(partnerFormData.email)
                                            }
                                            // readOnly={!partnerFormData.isNonItsPartner}
                                            readOnly={!partnerFormData.isNonItsPartner}
                                            onChange={(e) => {
                                                setPartnerFormData({ ...partnerFormData, email: e.target.value });
                                                setAddPartnerErrors?.((errors) => ({
                                                    ...errors,
                                                    email: undefined,
                                                }));
                                            }}
                                            placeholder="email@example.com"
                                            className={`${!partnerFormData.isNonItsPartner
                                                ? "cursor-not-allowed bg-gray-100"
                                                : ""
                                                }`}
                                        />
                                        {AddpartnerErrors.email && (
                                            <p className="text-xs text-red-600 mt-1">
                                                {AddpartnerErrors.email}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <InputFieldset
                                            label="Mobile Number"
                                            required
                                            id="partnerMobile"
                                            value={
                                                partnerFormData.isNonItsPartner
                                                    ? partnerFormData.mobileNumber
                                                    : maskMobileNumber(partnerFormData.mobileNumber)
                                            }
                                            readOnly={!partnerFormData.isNonItsPartner}
                                            onChange={(e) => {
                                                const value = validatePhoneNumberInput(e.target.value);
                                                setPartnerFormData({ ...partnerFormData, mobileNumber: value });
                                            }}
                                            placeholder="+964-XXX-XXXXXXX"
                                            className={`${!partnerFormData.isNonItsPartner
                                                ? "cursor-not-allowed bg-gray-100"
                                                : ""
                                                }`}
                                        />
                                        {AddpartnerErrors.mobileNumber && (
                                            <p className="text-xs text-red-600 mt-1">
                                                {AddpartnerErrors.mobileNumber}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Configurations */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-700 tracking-wide">Configurations</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {/* Member Type - Radio Group */}
                                <RadioGroup
                                    value={partnerFormData.isPartner ? 'partner' : partnerFormData.isOfficeStaff ? 'staff' : ''}
                                    onValueChange={(value: 'partner' | 'staff') => {
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            isPartner: value === 'partner',
                                            isOfficeStaff: value === 'staff'
                                        });
                                        setAddPartnerErrors?.((errors) => ({
                                            ...errors,
                                            memberType: undefined,
                                            documents: value === 'staff' ? undefined : errors.documents,
                                        }));
                                    }}
                                    className={isSoleProprietor ? "col-span-2 grid grid-cols-1 gap-4" : "col-span-2 grid grid-cols-2 gap-4"}
                                >
                                    {!isSoleProprietor && (
                                        <div className="flex items-center space-x-3 p-3 border-2 rounded-lg bg-blue-50 border-blue-200 hover:border-blue-300 transition-colors">
                                            <RadioGroupItem value="partner" id="isPartner" className="size-5" />
                                            <Label htmlFor="isPartner" className="text-sm cursor-pointer font-medium text-gray-900">
                                                Is Partner
                                            </Label>
                                        </div>
                                    )}
                                    <div className="flex items-center space-x-3 p-3 border-2 rounded-lg bg-purple-50 border-purple-200 hover:border-purple-300 transition-colors">
                                        <RadioGroupItem value="staff" id="isOfficeStaff" className="size-5" />
                                        <Label htmlFor="isOfficeStaff" className="text-sm cursor-pointer font-medium text-gray-900">
                                            Is Office Staff
                                        </Label>
                                    </div>
                                </RadioGroup>
                                {AddpartnerErrors.memberType && (
                                    <p className="col-span-2 -mt-2 text-xs text-red-600">
                                        {AddpartnerErrors.memberType}
                                    </p>
                                )}

                                <div className="flex items-center space-x-3 p-3 border rounded-lg bg-orange-50 border-orange-200">
                                    <Checkbox
                                        id="loginRequired"
                                        checked={partnerFormData.loginRequired}
                                        onCheckedChange={(checked) => setPartnerFormData({ ...partnerFormData, loginRequired: !!checked })}
                                    />
                                    <Label htmlFor="loginRequired" className="text-sm cursor-pointer font-medium">
                                        Login Required
                                    </Label>
                                </div>
                                <div className="flex items-center space-x-3 p-3 border rounded-lg bg-green-50 border-green-200">
                                    <Checkbox
                                        id="isAGL"
                                        checked={partnerFormData.isAuthorizedGroupLeader}
                                        onCheckedChange={(checked) => setPartnerFormData({ ...partnerFormData, isAuthorizedGroupLeader: !!checked })}
                                    />
                                    <Label htmlFor="isAGL" className="text-sm cursor-pointer font-medium">
                                        Authorized Group Leader
                                    </Label>
                                </div>
                            </div>
                        </div>

                        {/* Document Uploads - hidden only for office staff */}
                        {partnerFormData.isPartner && <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Document Uploads</h3>
                            <div className="space-y-3">
                                {partnerFormData.documents.map((doc: DocumentUpload) => (
                                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-700">
                                                {doc.name}
                                                {doc.isRequired && <span className="text-red-500 ml-1">*</span>}
                                            </p>
                                            <p className="text-xs text-red-400">
                                                {`Accepted format: ${doc?.acceptedFormats} (Max ${formatFileSizeFromKB(
                                                    doc?.maxFileSizeKB
                                                )})`}
                                            </p>
                                            {doc.file && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    <span className="text-green-600">✓</span> {doc.file.name}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <label className="cursor-pointer">
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept={doc.acceptedFormats
                                                        ? doc.acceptedFormats
                                                            .split(",")
                                                            .map((f) => "." + f.trim())
                                                            .join(",")
                                                        : "*"}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0] || null;
                                                        if (!file) return;
                                                        const isValid = validateFile(doc, file);
                                                        if (!isValid) {
                                                            e.target.value = ""; // reset input
                                                            return;
                                                        }
                                                        handlePartnerFileUpload(doc.id, file);
                                                    }}
                                                />
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-9"
                                                    asChild
                                                >
                                                    <span>
                                                        <Upload className="w-4 h-4 mr-2" />
                                                        {doc.file ? 'Change' : 'Upload'}
                                                    </span>
                                                </Button>
                                            </label>
                                            {doc.file && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handlePartnerFileUpload(doc.id, null)}
                                                    className="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {AddpartnerErrors.documents && (
                                    <p className="text-xs text-red-600 mt-1">
                                        {AddpartnerErrors.documents}
                                    </p>
                                )}
                            </div>
                        </div>}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setIsPartnerDialogOpen(false) }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSavePartner}
                            className="text-white"
                            style={{ backgroundColor: '#5B9BD5' }}
                        >
                            {'Add Member'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default AddNewPartnerPopup
